import React, {useState, useEffect, useRef, useCallback} from 'react';
import {useContextSelector} from 'use-context-selector';

import {Helmet} from 'react-helmet';
import {useTranslation} from 'react-i18next';

import {
    Button, Badge, Card, Form
} from 'react-bootstrap';

import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'

import {faSearch} from '@fortawesome/free-solid-svg-icons/faSearch';
import {faEye} from '@fortawesome/free-solid-svg-icons/faEye';
import {faTimes} from '@fortawesome/free-solid-svg-icons/faTimes';
import {faSyncAlt} from '@fortawesome/free-solid-svg-icons/faSyncAlt';
import {faWifi} from '@fortawesome/free-solid-svg-icons/faWifi';
import {faBuilding} from '@fortawesome/free-solid-svg-icons/faBuilding';

import useCreateClientStatusMessage from '../util/useCreateClientStatusMessage';
import createUUID from '../util/createUUID';
import {useBreakpoint} from '../util/handleBreakpoints';

import {LocalContext} from '../context/LocalContext';
import {SessionContext} from '../context/SessionContext';

import useGetFields from './useGetFields';

import useCheckFiltersValuesDiffer from './useCheckFiltersValuesDiffer'

import useRenderFiltersFragment from './useRenderFiltersFragment';

import useGetProvidersPage from './useGetProvidersPage';

import useSubmitProvidersSearchFilterAnalytics from './useSubmitProvidersSearchFilterAnalytics';

import ProvidersCheckout from './ProvidersCheckout';

import LayoutSidebar from '../common/LayoutSidebar';
import Layout from '../common/Layout';

import ContentBackgroundArt from '../common/ContentBackgroundArt';


// TODO: Move providers display and filters display to separate components/files

const Providers = () => {
    const isMounted = useRef(null);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        }
    }, []);

    const {t} = useTranslation();
    const breakpoints = useBreakpoint();
    const submitProvidersSearchFilterAnalytics = useSubmitProvidersSearchFilterAnalytics();

    const clientOnline = useContextSelector(LocalContext, v => v[0].clientOnline);
    const cookiesPolicyAccepted = useContextSelector(LocalContext, v => v[0].cookiesPolicy.accepted);
    const UILangGuest = useContextSelector(LocalContext, v => v[0].UILang);
    const UILangSession = useContextSelector(SessionContext, v => v[0].UILang);

    const [UILang, setUILang] = useState(null);

    const [hideSidebarCompact, setHideSidebarCompact] = useState(false);

    const [modeProvidersCheckout, setModeProvidersCheckout] = useState(false);

    const [providersSelected, setProvidersSelected] = useState([]);

    useEffect(() => {
        const currentUILang = cookiesPolicyAccepted ? UILangGuest : UILangSession;
        if (UILang !== currentUILang) {
            setUILang(currentUILang);
        }
    }, [cookiesPolicyAccepted, UILangGuest, UILangSession, setUILang, UILang]);

    const createClientStatusMessage = useCreateClientStatusMessage();

    const getFields = useGetFields();

    const checkFiltersValuesDiffer = useCheckFiltersValuesDiffer()

    const renderFiltersFragment = useRenderFiltersFragment();

    const [isLoadingAPIDataProviders, setIsLoadingAPIDataProviders] = useState(false);

    const [APIDataActiveFilters, setAPIDataActiveFilters] = useState({});
    const [langAPIDataActiveFilters, setLangAPIDataActiveFilters] = useState(null);
    const [isErrorGetAPIDataActiveFilters, setIsErrorGetAPIDataActiveFilters] = useState(false);
    const [isLoadingAPIDataActiveFilters, setIsLoadingAPIDataActiveFilters] = useState(false);
    const [isLoadedAPIDataActiveFilters, setIsLoadedAPIDataActiveFilters] = useState(false);
    const [isUpdatedAPIDataActiveFilters, setIsUpdatedAPIDataActiveFilters] = useState(false);

    const [fieldCategoryExpertises, setFieldCategoryExpertises] = useState(null);

    const handleGetAPIDataActiveFilters = useCallback(
        async () => {
            setIsLoadingAPIDataActiveFilters(true);
            setLangAPIDataActiveFilters(UILang);

            let responseAPIDataActiveFilters;

            try {
                responseAPIDataActiveFilters = await getFields({lang: UILang, returnActive: true});
            } catch (error) {
                if (isMounted.current) {
                    createClientStatusMessage(error.clientStatusMessage);
                    setIsErrorGetAPIDataActiveFilters(true);
                    setIsLoadingAPIDataActiveFilters(false);
                    return
                }
            }

            if (isMounted.current) {
                // console.log(responseAPIDataActiveFilters);
                setAPIDataActiveFilters(responseAPIDataActiveFilters);
            }
            if (isLoadedAPIDataActiveFilters) {
                setIsUpdatedAPIDataActiveFilters(true);
            } else {
                setIsLoadedAPIDataActiveFilters(true);
            }

            if (process.env.REACT_APP_EXPERTISES_CATEGORY_FIELD_NAME) {
                const expertisesCategory = responseAPIDataActiveFilters.category.find(value => value.position === process.env.REACT_APP_EXPERTISES_CATEGORY_FIELD_NAME);

                if (expertisesCategory) {
                    setFieldCategoryExpertises(expertisesCategory);
                }
            }


            setIsLoadingAPIDataActiveFilters(false);
        },
        [
            UILang,
            setIsErrorGetAPIDataActiveFilters,
            isLoadedAPIDataActiveFilters,
            setAPIDataActiveFilters,
            setIsUpdatedAPIDataActiveFilters,
            setIsLoadedAPIDataActiveFilters,
            setIsLoadingAPIDataActiveFilters,
            getFields,
            setFieldCategoryExpertises,
            createClientStatusMessage
        ]
    );

    useEffect(
        () => {
            if (
                clientOnline &&
                UILang !== null &&
                !isErrorGetAPIDataActiveFilters &&
                !isLoadingAPIDataActiveFilters &&
                (!isLoadedAPIDataActiveFilters || langAPIDataActiveFilters !== UILang)
            ) {
                setIsLoadingAPIDataActiveFilters(true);
                handleGetAPIDataActiveFilters();
            }
        },
        [
            clientOnline,
            UILang,
            isErrorGetAPIDataActiveFilters,
            isLoadingAPIDataActiveFilters,
            isLoadedAPIDataActiveFilters,
            langAPIDataActiveFilters,
            setIsLoadingAPIDataActiveFilters,
            handleGetAPIDataActiveFilters
        ]
    );

    const [loading, setLoading] = useState(false);

    useEffect(
        () => {
            const loadingData = (
                isLoadingAPIDataActiveFilters ||
                isLoadingAPIDataProviders
            );

            if (!loading && loadingData) {
                setLoading(true);
            } else if (loading && !loadingData) {
                setLoading(false);
            }
        },
        [
            isLoadingAPIDataActiveFilters,
            isLoadingAPIDataProviders,
            loading,
            setLoading
        ]
    );

    const [filtersInitial, setFiltersInitial] = useState({});
    const [filters, setFilters] = useState({});
    const [filtersApplied, setFiltersApplied] = useState({});
    const [searchText, setSearchText] = useState('');
    const [searchTextApplied, setSearchTextApplied] = useState('');

    const [areFiltersInitialized, setAreFiltersInitialized] = useState(false);

    useEffect(
        () => {
            if (
                !isErrorGetAPIDataActiveFilters &&
                !loading &&
                isLoadedAPIDataActiveFilters &&
                (
                    !areFiltersInitialized ||
                    isUpdatedAPIDataActiveFilters
                )
            ) {
                if (isUpdatedAPIDataActiveFilters) {
                    setIsUpdatedAPIDataActiveFilters(false);
                }

                if (!areFiltersInitialized) {
                    setAreFiltersInitialized(true);
                }

                const optionsFieldIndustry = [...Object.values(APIDataActiveFilters.industry).map(value => value.id)];
                const APIOptionsIndustry = {};
                Object.values(APIDataActiveFilters.industry).forEach(value => APIOptionsIndustry[value.id] = value.name);

                const optionsFieldCountry = [...Object.values(APIDataActiveFilters.country).map(value => value.id)];
                const APIOptionsCountry = {}
                Object.values(APIDataActiveFilters.country).forEach(value => APIOptionsCountry[value.id] = t(`country.${value.name}`));

                const categoryFilters = {};
                const categoryFiltersInitial = {};
                const categoryFiltersApplied = {};

                APIDataActiveFilters.category.forEach((category, index) => {
                    const name = `category-${category.position}`;
                    const type = category.multi_select_filters ? 'multi-select' : 'single-select';

                    const APIOptionsCategory = {};
                    const categoryOptions = Object.values(category.entity_list).map(entry => entry.id);
                    Object.values(category.entity_list).forEach(value => APIOptionsCategory[value.id] = value.name);

                    categoryFiltersInitial[name] = {
                        value: category.multi_select_filters ? [] : null,
                        type,
                        name,
                        APIField: 'entity',
                        label: () => category.name,
                        valid: true,
                        touched: false,
                        config: {
                            variant: 'dropdown',
                            options: categoryOptions,
                            APIOptions: APIOptionsCategory,
                        },
                        validation: [
                            ...(category.multi_select_filters ?
                                [
                                    {
                                        type: 'multiSelectMaxItems',
                                        value: 50
                                    }
                                ] : []
                            )
                        ],
                        errors: [],
                        index: index
                    }

                    categoryFilters[name] = {
                        value: filters.filters ? filters.filters[name].value : (category.multi_select_filters ? [] : null),
                        type,
                        name,
                        APIField: 'entity',
                        label: () => category.name,
                        valid: filters.filters ? filters.filters[name].valid : true,
                        touched: filters.filters ? filters.filters[name].touched : false,
                        config: {
                            variant: 'dropdown',
                            options: categoryOptions,
                            APIOptions: APIOptionsCategory,
                        },
                        validation: [
                            ...(category.multi_select_filters ?
                                [
                                    {
                                        type: 'multiSelectMaxItems',
                                        value: 50
                                    }
                                ] : []
                            )
                        ],
                        errors: filters.filters ? filters.filters[name].errors : [],
                        index: filters.filters ? filters.filters[name].index : index
                    }

                    categoryFiltersApplied[name] = {
                        value: filtersApplied.filters ? filtersApplied.filters[name].value : (category.multi_select_filters ? [] : null),
                        type,
                        name,
                        APIField: 'entity',
                        label: () => category.name,
                        valid: filtersApplied.filters ? filtersApplied.filters[name].valid : true,
                        touched: filtersApplied.filters ? filtersApplied.filters[name].touched : false,
                        config: {
                            variant: 'dropdown',
                            options: categoryOptions,
                            APIOptions: APIOptionsCategory,
                        },
                        validation: [
                            ...(category.multi_select_filters ?
                                [
                                    {
                                        type: 'multiSelectMaxItems',
                                        value: 50
                                    }
                                ] : []
                            )
                        ],
                        errors: filtersApplied.filters ? filtersApplied.filters[name].errors : [],
                        index: filtersApplied.filters ? filtersApplied.filters[name].index : index
                    }
                });

                const filtersData = {
                    valid: true,
                    changed: false,
                    ...filters,
                    filters: {
                        ...(filters.filters ? filters.filters : {}),
                        industry: {
                            value: null,
                            type: 'single-select',
                            name: "industry",
                            label: () => t('Providers.filters.industry'),
                            APIField: 'industry',
                            valid: true,
                            touched: false,
                            validation: [
                                {
                                    type: 'singleSelectNotEmpty'
                                }
                            ],
                            errors: [],
                            ...(filters.filters ? filters.filters.industry : {}),
                            config: {
                                variant: 'dropdown',
                                ...(filters.filters ? filters.filters.industry.config : {}),
                                options: optionsFieldIndustry,
                                APIOptions: APIOptionsIndustry
                            }
                        },
                        country: {
                            value: [],
                            type: 'multi-select',
                            name: 'country',
                            label: () => t('Providers.filters.country'),
                            APIField: 'country',
                            valid: true,
                            touched: false,
                            errors: [],
                            validation: [
                                {
                                    type: 'singleSelectNotEmpty'
                                }
                            ],
                            ...(filters.filters ? filters.filters.country : {}),
                            config: {
                                variant: 'dropdown',
                                ...(filters.filters ? filters.filters.country.config : {}),
                                options: optionsFieldCountry,
                                APIOptions: APIOptionsCountry
                            }
                        },
                        ...categoryFilters
                    }
                }

                const filtersInitialData = {
                    valid: true,
                    changed: false,
                    ...filtersInitial,
                    filters: {
                        ...(filtersInitial.filters ? filtersInitial.filters : {}),
                        industry: {
                            value: null,
                            type: 'single-select',
                            name: "industry",
                            label: () => t('Providers.filters.industry'),
                            APIField: 'industry',
                            valid: true,
                            touched: false,
                            validation: [
                                {
                                    type: 'singleSelectNotEmpty'
                                }
                            ],
                            errors: [],
                            ...(filtersInitial.filters ? filtersInitial.filters.industry : {}),
                            config: {
                                variant: 'dropdown',
                                ...(filtersInitial.filters ? filtersInitial.filters.industry.config : {}),
                                options: optionsFieldIndustry,
                                APIOptions: APIOptionsIndustry
                            }
                        },
                        country: {
                            value: [],
                            type: 'multi-select',
                            name: 'country',
                            label: () => t('Providers.filters.country'),
                            APIField: 'country',
                            valid: true,
                            touched: false,
                            errors: [],
                            validation: [
                                {
                                    type: 'singleSelectNotEmpty'
                                }
                            ],
                            ...(filtersInitial.filters ? filtersInitial.filters.country : {}),
                            config: {
                                variant: 'dropdown',
                                ...(filtersInitial.filters ? filtersInitial.filters.country.config : {}),
                                options: optionsFieldCountry,
                                APIOptions: APIOptionsCountry
                            }
                        },
                        ...categoryFiltersInitial
                    }
                }

                const filtersAppliedData = {
                    valid: true,
                    changed: false,
                    ...filtersApplied,
                    filters: {
                        ...(filtersApplied.filters ? filtersApplied.filters : {}),
                        industry: {
                            value: null,
                            type: 'single-select',
                            name: "industry",
                            label: () => t('Providers.filters.industry'),
                            APIField: 'industry',
                            valid: true,
                            touched: false,
                            validation: [
                                {
                                    type: 'singleSelectNotEmpty'
                                }
                            ],
                            errors: [],
                            ...(filtersApplied.filters ? filtersApplied.filters.industry : {}),
                            config: {
                                variant: 'dropdown',
                                ...(filtersApplied.filters ? filtersApplied.filters.industry.config : {}),
                                options: optionsFieldIndustry,
                                APIOptions: APIOptionsIndustry
                            }
                        },
                        country: {
                            value: [],
                            type: 'multi-select',
                            name: 'country',
                            label: () => t('Providers.filters.country'),
                            APIField: 'country',
                            valid: true,
                            touched: false,
                            errors: [],
                            validation: [
                                {
                                    type: 'singleSelectNotEmpty'
                                }
                            ],
                            ...(filtersApplied.filters ? filtersApplied.filters.country : {}),
                            config: {
                                variant: 'dropdown',
                                ...(filtersApplied.filters ? filtersApplied.filters.country.config : {}),
                                options: optionsFieldCountry,
                                APIOptions: APIOptionsCountry
                            }
                        },
                        ...categoryFiltersApplied
                    }
                }

                setFilters(filtersData);
                setFiltersApplied(filtersAppliedData);
                setFiltersInitial(filtersInitialData);
            }
        },
        [
            isUpdatedAPIDataActiveFilters,
            isErrorGetAPIDataActiveFilters,
            loading,
            isLoadedAPIDataActiveFilters,
            areFiltersInitialized,
            setAreFiltersInitialized,
            filters,
            filtersApplied,
            filtersInitial,
            setFilters,
            setFiltersApplied,
            setFiltersInitial,
            APIDataActiveFilters,
            t
        ]
    );

    const [filtersAppliedDiffer, setFiltersAppliedDiffer] = useState(false);
    const [filtersInitialDiffer, setFiltersInitialDiffer] = useState(false);

    useEffect(
        () => {
            if (filters.changed) {
                setFilters({...filters, changed: false});

                const {fieldAppliedValuesDiffer, fieldInitialValuesDiffer} = checkFiltersValuesDiffer(filters, filtersInitial, filtersApplied);

                if (fieldAppliedValuesDiffer) {
                    setFiltersAppliedDiffer(true);
                } else {
                    setFiltersAppliedDiffer(false);
                }

                if (fieldInitialValuesDiffer) {
                    setFiltersInitialDiffer(true);
                } else {
                    setFiltersInitialDiffer(false);
                }
            }
        },
        [
            filters,
            filtersApplied,
            filtersInitial,
            setFiltersInitialDiffer,
            setFilters,
            checkFiltersValuesDiffer
        ]
    );

    const [APIDataProviders, setAPIDataProviders] = useState({filters: {}, providers: []});
    const [isErrorGetAPIDataProviders, setIsErrorGetAPIDataProviders] = useState(false);
    const [isLoadedAPIDataProviders, setIsLoadedAPIDataProviders] = useState(false);

    const selectProvidersCountMax = Math.max([process.env.REACT_APP_PROVIDERS_SELECT_CONTACT_MAX_COUNT, process.env.REACT_APP_PROVIDERS_SELECT_PDF_SUMMARY_MAX_COUNT]);

    const getProviders = useGetProvidersPage();

    const handleGetAPIDataProvidersInitial = useCallback(
        async () => {
            setIsLoadingAPIDataProviders(true);

            let responseProviders;

            try {
                responseProviders = await getProviders({
                    filters: null,
                    searchText: '',
                    page: 1,
                    userInteractionSearch: false
                });
            } catch (error) {
                if (isMounted.current) {
                    setIsErrorGetAPIDataProviders(true);
                    createClientStatusMessage(error.clientStatusMessage);
                    setIsLoadingAPIDataProviders(false);
                    return
                }
            }

            // console.log(responseProviders);

            if (isMounted.current) {
                setAPIDataProviders({
                    ...APIDataProviders,
                    current: 1,
                    filters: {},
                    searchText: '',
                    prev: null,
                    next: responseProviders.next ? 2 : null,
                    count: responseProviders.count,
                    providers: responseProviders.results
                })

                // console.log(responseProviders.results);

                if (responseProviders.results.length > 0) {
                    const element = document.getElementById(`provider-${responseProviders.results[0].id}`);

                    if (element) {
                        element.scrollIntoView({behavior: "smooth", block: "center", inline: "nearest"});
                    }
                }

                setIsLoadedAPIDataProviders(true);
                setSearchTextApplied(searchText);
                setIsLoadingAPIDataProviders(false);
            }
        },
        [
            setIsLoadingAPIDataProviders,
            setIsErrorGetAPIDataProviders,
            createClientStatusMessage,
            setAPIDataProviders,
            APIDataProviders,
            setIsLoadedAPIDataProviders,
            getProviders
        ]
    );

    const handleGetAPIDataProvidersNext = useCallback(
        async () => {
            setIsLoadingAPIDataProviders(true);

            let responseProviders;

            try {
                responseProviders = await getProviders({
                    filters: APIDataProviders.filters,
                    searchText: APIDataProviders.searchText || '',
                    page: APIDataProviders.current + 1,
                    userInteractionSearch: false
                });
            } catch (error) {
                if (isMounted.current) {
                    setIsErrorGetAPIDataProviders(true);
                    createClientStatusMessage(error.clientStatusMessage);
                    setIsLoadingAPIDataProviders(false);
                    return
                }
            }

            if (isMounted.current) {
                setAPIDataProviders({
                    ...APIDataProviders,
                    current: APIDataProviders.current + 1,
                    filters: APIDataProviders.filters,
                    searchText: APIDataProviders.searchText || '',
                    prev: responseProviders.prev ? APIDataProviders.current - 1 : null,
                    next: responseProviders.next ? APIDataProviders.current + 1 : null,
                    count: responseProviders.count,
                    providers: [
                        ...APIDataProviders.providers,
                        ...responseProviders.results
                    ]
                });

                const element = document.getElementById(`provider-${responseProviders.results[0].id}`);

                if (element) {
                    element.scrollIntoView({behavior: "smooth", block: "center", inline: "nearest"});
                }

                setIsLoadingAPIDataProviders(false);
            }
        },
        [
            setIsLoadingAPIDataProviders,
            setIsErrorGetAPIDataProviders,
            createClientStatusMessage,
            APIDataProviders,
            setAPIDataProviders,
            getProviders
        ]
    );


    const handleGetAPIDataFilteredProviders = useCallback(
        async () => {
            setIsLoadingAPIDataProviders(true);

            let responseProviders;

            try {
                responseProviders = await getProviders({
                    filters: filters.filters,
                    searchText,
                    page: 1,
                    userInteractionSearch: true
                });
            } catch (error) {
                if (isMounted.current) {
                    setIsErrorGetAPIDataProviders(true);
                    createClientStatusMessage(error.clientStatusMessage);
                    setIsLoadingAPIDataProviders(false);
                    return
                }
            }

            if (process.env.REACT_APP_GOOGLE_ANALYTICS_ID) {
                try {
                    submitProvidersSearchFilterAnalytics({...filters.filters, searchText});
                } catch (error) {
                    console.log(error);
                }
            }

            if (isMounted.current) {
                setAPIDataProviders({
                    ...APIDataProviders,
                    current: 1,
                    filters: filters.filters,
                    searchText,
                    prev: null,
                    next: responseProviders.next ? 2 : null,
                    count: responseProviders.count,
                    providers: responseProviders.results
                });

                if (responseProviders.results.length > 0) {
                    const element = document.getElementById(`provider-${responseProviders.results[0].id}`);

                    if (element) {
                        element.scrollIntoView({behavior: "smooth", block: "center", inline: "nearest"});
                    }
                }

                setIsLoadingAPIDataProviders(false);
            }
        },
        [
            submitProvidersSearchFilterAnalytics,
            setIsLoadingAPIDataProviders,
            setIsErrorGetAPIDataProviders,
            createClientStatusMessage,
            APIDataProviders,
            setAPIDataProviders,
            getProviders,
            filters
        ]
    );

    useEffect(
        () => {
            if (
                clientOnline &&
                !isLoadingAPIDataProviders &&
                !isLoadedAPIDataProviders &&
                !isErrorGetAPIDataProviders
            ) {
                handleGetAPIDataProvidersInitial();
            }
        },
        [
            clientOnline,
            isLoadingAPIDataProviders,
            isLoadedAPIDataProviders,
            isErrorGetAPIDataProviders,
            handleGetAPIDataFilteredProviders,
            handleGetAPIDataProvidersInitial
        ]
    );

    const handleResetSearchState = useCallback(
        () => {
            setProvidersSelected([]);
            setFilters({...filtersInitial});
            setSearchText('');
            setSearchTextApplied('');
            setFiltersAppliedDiffer(false);
            setFiltersInitialDiffer(false);
            handleGetAPIDataProvidersInitial();
        },
        [
            setProvidersSelected,
            setFilters,
            filtersInitial,
            setFiltersInitialDiffer,
            setFiltersAppliedDiffer,
            handleGetAPIDataProvidersInitial
        ]
    );


    const handleApplyProvidersSearch = useCallback(
        () => {
            setHideSidebarCompact(true);
            setFiltersApplied({...filters, changed: false});
            setFiltersAppliedDiffer(false);
            handleGetAPIDataFilteredProviders();
        },
        [
            setHideSidebarCompact,
            setFiltersApplied,
            filters,
            setFiltersAppliedDiffer,
            handleGetAPIDataFilteredProviders
        ]
    );

    const handleClearProvidersSearchText = useCallback(
        () => {
            setSearchText('');
        },
        [setSearchText]
    );

    return <>
        <style>
            {
                `
                    .btn .providers__sidebar__sidebarToggleContent .badge {
                        background-color: var(--primary);
                        color: white;
                    }

                    .btn:hover .providers__sidebar__sidebarToggleContent .badge {
                        background-color: white;
                        color: var(--primary);
                    }


                    .btn.active .providers__sidebar__sidebarToggleContent .badge {
                        background-color: white;
                        color: var(--primary);
                    }

                    .btn.active:hover .providers__sidebar__sidebarToggleContent .badge {
                        background-color: white;
                        color: var(--primary);
                    }
                `
            }
        </style>

        <LayoutSidebar
            sidebarCompactVisibility={[hideSidebarCompact, setHideSidebarCompact]}
            sidebarContent={
                <Form
                    className="providers__sidebar__form-container"
                    style={
                        {
                            display: 'flex',
                            flexDirection: 'column',
                            height: '100%',
                            flexShrink: 1,
                            maxHeight: 'calc(100vh - 44px)',
                            background: 'white',
                            position: 'relative'
                        }
                    }
                >
                    <div
                        style={
                            {
                                display: 'flex',
                                overflow: 'auto',
                                flexShrink: 1,
                                flexGrow: 1
                            }
                        }
                    >
                        <div
                            style={
                                {
                                    display: 'flex',
                                    flexDirection: 'column',
                                    maxWidth: breakpoints.md ? '540px' : '100%',
                                    width: `100%`,
                                    marginLeft: 'auto',
                                    marginRight: 'auto',
                                }
                            }
                        >
                            {
                                areFiltersInitialized &&
                                <>
                                    <div
                                        style={
                                            {
                                                padding: '2rem 2rem 1rem 2rem',
                                            }
                                        }
                                    >
                                        <label className="font-weight-bold d-block mb-2" htmlFor="providers-search-text" style={{fontSize: "28px", fontWeight: 700, fontFamily: "Ubuntu, sans-serif"}}>
                                            {t('Providers.filters.search-label')}
                                        </label>
                                        <div style={{position: 'relative'}}>
                                            <input
                                                id="providers-search-text"
                                                type="text"
                                                className="form-control border-radius-10 p-4"
                                                placeholder={t('Providers.filters.search-placeholder')}
                                                value={searchText}
                                                onChange={(event) => setSearchText(event.target.value)}
                                                onKeyDown={(event) => {
                                                    if (event.key === 'Enter' && clientOnline && filters.valid && loading === false && (filtersAppliedDiffer || searchText.trim() !== searchTextApplied.trim())) {
                                                        event.preventDefault();
                                                        handleApplyProvidersSearch();
                                                    }
                                                }}
                                            />
                                            {
                                                searchText.length > 0 &&
                                                <button
                                                    type="button"
                                                    aria-label="Clear search"
                                                    onClick={handleClearProvidersSearchText}
                                                    style={{
                                                        position: 'absolute',
                                                        right: '16px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        border: 'none',
                                                        background: 'transparent',
                                                        color: '#495057',
                                                        padding: 0,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faTimes} />
                                                </button>
                                            }
                                        </div>
                                    </div>
                                    <div
                                        style={
                                            {
                                                display: 'flex',
                                                alignItems: 'center',
                                                padding: '1rem 2rem 2rem 2rem',
                                            }
                                        }
                                    >
                                        <h4 className="mb-0 text-muted" style={{fontSize: "16px", fontWeight: 700, fontFamily: "Ubuntu, sans-serif"}}>
                                            <span>{t('Providers.filters.search-filters')}</span>
                                        </h4>
                                    </div>
                                    <div
                                        style={
                                            {
                                                paddingBottom: '5rem',
                                                marginTop: '-1rem'
                                            }
                                        }
                                    >
                                        {renderFiltersFragment({
                                            filters,
                                            setFilters,
                                            filtersInitial,
                                            loading
                                        })}
                                    </div>
                                </>
                            }
                        </div>
                    </div>

                    {
                        filtersAppliedDiffer || filtersInitialDiffer ?
                            <div
                                style={
                                    {
                                        display: 'flex',
                                        flexDirection: "row",
                                        flexWrap: 'wrap',
                                        zIndex: 2,
                                        position: 'relative',
                                        background: 'white',
                                        borderTop: '1px solid #ddd',
                                    }
                                }
                                className="px-1"
                            >
                                {
                                    (filtersInitialDiffer || searchText.length > 0 || searchTextApplied.length > 0) ?
                                        <div
                                            style={
                                                {
                                                    display: 'flex',
                                                    marginTop: 'auto',
                                                    flexShrink: 0,
                                                    flexGrow: 0,
                                                    height: '56px',
                                                }
                                            }
                                            className="py-2 px-1 m-0"
                                        >
                                            <Button
                                                variant="dark"
                                                className="btn-block"
                                                disabled={loading || (!filtersInitialDiffer && searchText.length === 0 && searchTextApplied.length === 0)}
                                                onClick={
                                                    () => {
                                                        setHideSidebarCompact(true);
                                                        setFilters({...filtersInitial, changed: false});
                                                        setFiltersApplied({...filtersInitial, changed: false});
                                                        setSearchText('');
                                                        setSearchTextApplied('');
                                                        setFiltersAppliedDiffer(false);
                                                        setFiltersInitialDiffer(false);

                                                        if (filtersInitialDiffer && !filtersAppliedDiffer) {
                                                            handleGetAPIDataProvidersInitial();
                                                        }
                                                    }
                                                }
                                            >
                                                <span>
                                                    {t('Providers.reset-filters')}
                                                </span>
                                            </Button>
                                        </div> : null
                                }

                                <div
                                    style={
                                        {
                                            display: 'flex',
                                            marginTop: 'auto',
                                            flexShrink: 0,
                                            flexGrow: 1,
                                            height: '56px',
                                        }
                                    }
                                    className="py-2 px-1 m-0"
                                >
                                    <Button
                                        className="btn-block"
                                        variant="primary"
                                        disabled={!clientOnline || !filters.valid || loading || (!filtersAppliedDiffer && searchText.trim() === searchTextApplied.trim())}
                                        onClick={
                                            () => {
                                                handleApplyProvidersSearch();
                                            }
                                        }
                                    >
                                        <span
                                            className="mr-2"
                                        >
                                            <FontAwesomeIcon icon={faSearch} />
                                        </span>
                                        <span>
                                            {t('Providers.apply-filters')}
                                        </span>
                                    </Button>
                                </div>
                            </div> : null
                    }
                </Form>
            }

            sidebarToggleTitleShow={
                <span className="providers__sidebar__sidebarToggleContent">
                    <FontAwesomeIcon icon={faSearch} />
                    <span className="ml-2">{t('Providers.providers-filter-menu-show')}</span>
                </span>
            }

            sidebarToggleTitleHide={
                <span className="providers__sidebar__sidebarToggleContent">
                    <FontAwesomeIcon icon={faTimes} />
                    <span className="ml-2">{t('Providers.providers-filter-menu-hide')}</span>
                </span>
            }

            sidebarToggleMeta={
                <Button
                    disabled={modeProvidersCheckout || providersSelected.length === 0}
                    title={t('Providers.view-selected-meta')}
                    onClick={
                        () => setModeProvidersCheckout(true)
                    }
                >
                    <span>
                        <FontAwesomeIcon icon={faEye} />
                    </span>
                    <Badge
                        pill
                        variant="light"
                        className="text-primary ml-2"
                    >
                        {providersSelected.length}
                    </Badge>
                </Button>
            }

        >
            <Helmet><title>{t('Providers.page-head-title')}</title></Helmet>

            {
                modeProvidersCheckout ?
                    <ProvidersCheckout
                        setModeProvidersCheckout={setModeProvidersCheckout}
                        providersFetched={APIDataProviders.providers}
                        providersSelected={providersSelected}
                        setProvidersSelected={setProvidersSelected}
                        handleResetProvidersState={handleResetSearchState}
                        filters={filters.filters}
                    /> :
                    null
            }

            <Layout style={{background: "var(--light)"}}>
                <ContentBackgroundArt fill="#f6f6f6" />
                <Layout
                    style={
                        {
                            position: 'relative',
                            paddingTop: '1rem',
                            background: 'transparent'
                        }
                    }
                >
                    {
                        !clientOnline && !isLoadedAPIDataProviders ?
                            <Layout
                                style={
                                    {
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }
                                }
                            >
                                <div
                                    className="p-4 text-muted"
                                >
                                    <span><FontAwesomeIcon icon={faWifi} /></span>
                                    <span
                                        className="ml-2"
                                    >{t('Providers.message-offline')}</span>
                                </div>
                            </Layout> : <>
                                <div
                                    style={
                                        {
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            margin: '1rem 2rem 0 2rem',
                                            alignItems: 'center',
                                            minHeight: '2rem',
                                            flexShrink: 0
                                        }
                                    }
                                >
                                    {
                                        isLoadedAPIDataProviders ?
                                            <div
                                                style={
                                                    {
                                                        marginRight: '4rem',
                                                    }
                                                }
                                                className="text-muted"
                                            >
                                                <span>
                                                    {APIDataProviders.count}
                                                </span><span
                                                >{t('Providers.x-results')}</span>
                                            </div> : null
                                    }
                                    {
                                        Object.values(APIDataProviders.filters).filter(filter => filter.touched).length > 0 ?
                                            <div
                                                style={
                                                    {
                                                        display: 'flex',
                                                        flexWrap: 'wrap',
                                                        alignItems: 'center',
                                                        marginLeft: 'auto'
                                                    }
                                                }
                                            >
                                                {
                                                    Object.values(APIDataProviders.filters).filter(filter => filter.touched).map((filter) => {
                                                        let valueFragment = '';
                                                        switch (filter.type) {
                                                            case 'order':
                                                                break
                                                            case 'multi-select':
                                                                switch (filter.config.variant) {
                                                                    case 'list':
                                                                        if (true) {

                                                                        }
                                                                        break
                                                                    case 'dropdown':
                                                                        if (true) {
                                                                            const entries = []
                                                                            filter.value.forEach((entry, index) => {
                                                                                entries.push(<span key={createUUID()}>{filters.filters[filter.name].config.APIOptions[entry.value]}{index !== filter.value.length - 1 ? ', ' : null}</span>)
                                                                            });

                                                                            valueFragment = <><span>{entries}</span></>
                                                                        }
                                                                        break
                                                                    default:
                                                                        break
                                                                }
                                                                break
                                                            case 'single-select':
                                                                switch (filter.config.variant) {
                                                                    case 'list':
                                                                        if (true) {

                                                                        }
                                                                        break
                                                                    case 'dropdown':
                                                                        if (true) {
                                                                            valueFragment = <><span>{filters.filters[filter.name].config.APIOptions[filter.value]}</span></>
                                                                        }
                                                                        break
                                                                    default:
                                                                        break
                                                                }
                                                                break
                                                            case 'range':
                                                                if (true) {

                                                                }
                                                                break
                                                            default:
                                                                break
                                                        }

                                                        return <div
                                                            style={
                                                                {
                                                                    display: 'flex',
                                                                    flexGrow: 0,
                                                                    borderColor: 'transparent',
                                                                    padding: '0.25rem 0'
                                                                }

                                                            }
                                                            key={createUUID()}
                                                        >
                                                            <div
                                                                style={
                                                                    {
                                                                        fontSize: '0.8rem',
                                                                        marginRight: '1.25rem'
                                                                    }
                                                                }
                                                            >
                                                                <span>{filter.label()}: </span>
                                                                <span>{valueFragment}</span>
                                                            </div>
                                                        </div>
                                                    })
                                                }
                                                <div>
                                                    <Button
                                                        variant={"dark"}
                                                        className="btn-block"
                                                        size="sm"
                                                        disabled={loading || !filtersInitialDiffer}
                                                        onClick={
                                                            () => {
                                                                setFilters({...filtersInitial, changed: false});
                                                                setFiltersApplied({...filtersInitial, changed: false});
                                                                setFiltersAppliedDiffer(false);
                                                                setFiltersInitialDiffer(false);
                                                                handleGetAPIDataProvidersInitial();
                                                            }
                                                        }

                                                    >
                                                        {t('Providers.reset-filters')}
                                                    </Button>
                                                </div>
                                            </div> : null
                                    }
                                </div>
                                <div
                                    style={
                                        {
                                            display: 'grid',
                                            justifyItems: 'center',
                                            gridTemplateColumns: (breakpoints.sm ? `auto` : `repeat(auto-fit, minmax(500px, 1fr))`),
                                            gridAutoRows: 'minmax(0, max-content)',
                                            gridAutoFlow: 'dense',
                                            gridGap: '1.25rem',
                                            flexGrow: 1,
                                            flexShrink: 0,
                                            position: 'relative',
                                            padding: '1.5rem 2rem',
                                            marginTop: '0rem'
                                        }
                                    }
                                >
                                    <style>
                                        {
                                            `
                                                .provider-list__provider__expertises__expertise {
                                                    color: var(--secondary);
                                                }

                                                .provider-list__provider__expertises__expertise:not(:last-of-type) {
                                                }

                                                .provider-list__provider__expertises__expertise:not(:last-of-type):after {
                                                    content: ', ';
                                                    font-weight: 400;
                                                }

                                                .provider-list__provider__img-link__hover-notice {
                                                    visibility: hidden;
                                                }
                                                .provider-list__provider__img-link:hover .provider-list__provider__img-link__hover-notice {
                                                    visibility: visible;

                                                }

                                                .provider-list__details-snippet__item:not(:last-of-type) {
                                                    margin-right: 0.5em;
                                                }

                                                .provider-list__details-snippet__item:not(:first-of-type):before {
                                                    content: '/';
                                                    color: lightgray;
                                                    padding-right: 0.5em;
                                                }
                                            `
                                        }
                                    </style>

                                    {APIDataProviders.providers.map((provider) => {
                                        const isSelected = providersSelected.find(providerEntry => providerEntry.id === provider.id);
                                        return <Card
                                            key={`provider-${provider.id}`}
                                            id={`provider-${provider.id}`}
                                            style={
                                                {

                                                    maxWidth: '800px',
                                                    width: '100%',
                                                    display: 'flex',
                                                    overflow: 'hidden',
                                                    borderRadius: '1.65rem',
                                                    border: '3px solid #eaeaea',
                                                    boxShadow: '1px 1px 8px 0px rgba(0,0,0,0.035)',
                                                    ...(
                                                        isSelected ?
                                                            {
                                                                borderColor: "var(--primary)"
                                                            } :
                                                            {borderColor: '#eaeaea'}
                                                    )
                                                }
                                            }
                                        >
                                            <style>
                                                {
                                                    `
                                                            .provider-list__provider__title a:hover {
                                                                color: var(--primary) !important;
                                                            }
                                                        `
                                                }
                                            </style>
                                            <div
                                                key={createUUID()}
                                                style={
                                                    {
                                                        position: 'relative',
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        height: '100%'
                                                    }
                                                }
                                            >
                                                <div
                                                    style={
                                                        {
                                                            position: 'relative',
                                                            display: 'flex',
                                                            flexGrow: 1,
                                                            flexWrap: 'wrap',
                                                            paddingLeft: '1.75rem',
                                                            paddingBottom: '1rem'
                                                        }
                                                    }

                                                >
                                                    <div
                                                        style={
                                                            {
                                                                padding: '1.75rem',
                                                                paddingLeft: 0,
                                                                paddingBottom: 0,
                                                                flexGrow: 1,
                                                                flexShrink: 1,
                                                                flexBasis: '12rem'
                                                            }
                                                        }
                                                    >
                                                        <div
                                                            style={
                                                                {
                                                                    maxWidth: '14rem'
                                                                }
                                                            }
                                                        >

                                                            <div
                                                                style={
                                                                    {
                                                                        height: 0,
                                                                        paddingTop: '100%',
                                                                        marginBottom: 0,
                                                                        position: 'relative',
                                                                    }
                                                                }
                                                            >
                                                                <a
                                                                    style={
                                                                        {
                                                                            display: 'flex',
                                                                            position: 'absolute',
                                                                            background: '#ddd',
                                                                            top: 0,
                                                                            left: 0,
                                                                            right: 0,
                                                                            borderRadius: '1rem',
                                                                            overflow: 'hidden',
                                                                            bottom: 0,
                                                                            alignItems: 'center',
                                                                            justifyContent: 'center',
                                                                            color: '#bbb',
                                                                            boxShadow: '0px 0px 15px 0px rgba(0,0,0,0.075)',
                                                                        }
                                                                    }
                                                                    className="provider-list__provider__img-link"
                                                                    href={`/provider/${provider.id}`}
                                                                    title={`${t('Providers.show-provider-in-new-tab.p1')}${provider.title}${t('Providers.show-provider-in-new-tab.p2')}`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                >
                                                                    {provider.logo ?
                                                                        <img
                                                                            style={
                                                                                {
                                                                                    width: '100%',
                                                                                    height: '100%',
                                                                                    objectFit: 'cover',

                                                                                }
                                                                            }
                                                                            alt={`Branding for ${provider.title}`}
                                                                            src={`${process.env.REACT_APP_HOST}${provider.logo}`}
                                                                        /> : <span
                                                                            style={
                                                                                {
                                                                                    fontSize: '2rem',
                                                                                    display: 'inline-flex'
                                                                                }
                                                                            }
                                                                        ><FontAwesomeIcon icon={faBuilding} /></span>
                                                                    }
                                                                </a>
                                                            </div>
                                                        </div>

                                                    </div>

                                                    <div
                                                        style={
                                                            {
                                                                padding: '1.75rem',
                                                                paddingLeft: 0,
                                                                paddingBottom: 0,
                                                                flexBasis: '18rem',
                                                                flexGrow: 2,
                                                                flexShrink: 1,
                                                            }
                                                        }
                                                    >
                                                        <div className="d-flex"
                                                            style={
                                                                {
                                                                    flexGrow: 0,
                                                                    wordBreak: 'break-word',
                                                                    display: 'flex',
                                                                    flexDirection: 'column'
                                                                }
                                                            }
                                                        >
                                                            <h4
                                                                className={
                                                                    `provider-list__provider__title`

                                                                }
                                                                style={
                                                                    {
                                                                        color: "var(--primary)"
                                                                    }
                                                                }
                                                            >
                                                                <a
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="d-inline "
                                                                    title={`${t('Providers.show-provider-in-new-tab.p1')}${provider.title}${t('Providers.show-provider-in-new-tab.p2')}`}
                                                                    href={`/provider/${provider.id}`}
                                                                    style={
                                                                        {
                                                                            maxWidth: '100%',
                                                                            overflow: 'hidden',
                                                                        }
                                                                    }
                                                                >
                                                                    {provider.title}
                                                                </a>
                                                            </h4>
                                                        </div>
                                                        {isLoadedAPIDataActiveFilters && provider.industry ?
                                                            <p className="text-muted mt-0 mb-0"
                                                                style={
                                                                    {
                                                                        fontSize: "0.75rem"
                                                                    }
                                                                }
                                                            >
                                                                <span className="provider-list__details-snippet__item">
                                                                    <span>{t(`country.${provider.country.name}`)}</span>
                                                                </span>
                                                                <span className="provider-list__details-snippet__item">
                                                                    <span>{APIDataActiveFilters.industry.find(value => value.id === provider.industry.id)?.name}</span>
                                                                </span>
                                                            </p> : null}

                                                        <p className="mt-3 mb-2"
                                                            style={
                                                                {
                                                                    lineHeight: 1.75
                                                                }
                                                            }
                                                        >
                                                            {provider.short_description}
                                                        </p>

                                                    </div>
                                                </div>
                                                <div
                                                    style={
                                                        {
                                                            paddingTop: 0,
                                                            paddingBottom: '1rem',
                                                            paddingLeft: '1.75rem',
                                                            paddingRight: '1.25rem',
                                                            alignSelf: 'flex-end',
                                                            flexShrink: 1,
                                                            flexGrow: 0,
                                                            width: '100%',
                                                            lineHeight: 1.5,
                                                            marginTop: 'auto',
                                                            display: 'flex',
                                                        }
                                                    }
                                                // className="border-top bg-light"
                                                >

                                                    {
                                                        fieldCategoryExpertises ?
                                                            <div
                                                                style={{
                                                                    display: 'flex',
                                                                    alignItems: 'center'
                                                                }}
                                                            >
                                                                <span
                                                                    style={
                                                                        {
                                                                            fontSize: '0.75rem',
                                                                        }
                                                                    }
                                                                >
                                                                    {
                                                                        (() => {
                                                                            const fragment = provider.entity.map(entity => {
                                                                                const match = fieldCategoryExpertises.entity_list.find(value => {
                                                                                    return value.id === entity.id
                                                                                });

                                                                                if (match) {
                                                                                    return <span
                                                                                        key={createUUID()}
                                                                                        className="provider-list__provider__expertises__expertise">
                                                                                        {match.name}
                                                                                    </span>
                                                                                }
                                                                                return null
                                                                            })
                                                                            if (fragment.length > 0) {
                                                                                return <>
                                                                                    <span className="text-muted" style={{fontWeight: 500}}>{fieldCategoryExpertises.name}: </span>
                                                                                    {fragment}
                                                                                </>
                                                                            } else {
                                                                                return <></>
                                                                            }
                                                                        })()
                                                                    }
                                                                </span>
                                                            </div> :
                                                            null
                                                    }
                                                    <div
                                                        style={
                                                            {
                                                                display: 'flex',
                                                                marginLeft: 'auto',
                                                                flexGrow: 0,
                                                                flexShrink: 0,
                                                                marginBottom: 'auto',
                                                                paddingLeft: '1.5rem'
                                                            }
                                                        }
                                                    >
                                                        <Button
                                                            variant={isSelected ? "outline-primary" : "primary"}
                                                            className={`${breakpoints.xs ? "btn-block" : "float-right"}`}
                                                            size="sm"
                                                            style={
                                                                {
                                                                }
                                                            }
                                                            onClick={
                                                                isSelected ?
                                                                    () => {
                                                                        setProvidersSelected([...providersSelected.filter(providerEntry => providerEntry.id !== provider.id)]);
                                                                    } :
                                                                    () => {
                                                                        setProvidersSelected([...providersSelected, provider]);
                                                                    }
                                                            }
                                                            disabled={
                                                                isSelected ?
                                                                    false :
                                                                    providersSelected.length >= selectProvidersCountMax
                                                            }
                                                        >
                                                            {
                                                                isSelected ?
                                                                    <>
                                                                        <span>
                                                                            <span
                                                                                className="mr-2 font-weight-bold"
                                                                            >
                                                                                {t('Providers.unselect')}
                                                                            </span>
                                                                            <FontAwesomeIcon
                                                                                icon={faTimes}
                                                                            />
                                                                        </span>
                                                                    </> :
                                                                    <>
                                                                        <FontAwesomeIcon
                                                                            icon={faEye}
                                                                        />
                                                                        <span
                                                                            className="ml-2"
                                                                        >
                                                                            {t('Providers.select')}
                                                                        </span>
                                                                    </>
                                                            }
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        </Card>
                                    })}
                                </div>
                            </>
                    }

                    <div
                        style={
                            {
                                display: 'flex',
                                flexDirection: 'column',
                                paddingBottom: '3rem',
                                paddingTop: '3.5rem'
                            }
                        }
                        className="px-4"
                    >
                        {
                            APIDataProviders.next ?
                                <div>
                                    <Button
                                        className="d-flex ml-auto mr-auto"
                                        variant="outline-primary"
                                        disabled={!clientOnline || loading || !isLoadedAPIDataProviders}
                                        onClick={
                                            async () => {
                                                await handleGetAPIDataProvidersNext();
                                            }
                                        }
                                    >
                                        <span
                                            className="d-flex ml-auto mr-auto"
                                        >
                                            {isLoadingAPIDataProviders ? t('Providers.more-results-loading') : t('Providers.more-results')}
                                            {isLoadingAPIDataProviders ? <span className="ml-2">
                                                <FontAwesomeIcon
                                                    spin={isLoadingAPIDataProviders}
                                                    size="sm"
                                                    icon={faSyncAlt}
                                                />
                                            </span> : null}
                                        </span>
                                    </Button>
                                </div>
                                : (isLoadedAPIDataProviders ? (
                                    APIDataProviders.providers.length > 0 ?
                                        <span
                                            className="ml-auto mr-auto text-muted"
                                            style={
                                                {
                                                    fontSize: '0.8rem'
                                                }
                                            }
                                        >
                                            {t('Providers.results-loaded')}
                                        </span> :
                                        <span
                                            className="ml-auto mr-auto text-muted"
                                            style={
                                                {
                                                    fontSize: '0.8rem'
                                                }
                                            }
                                        >
                                            {t('Providers.nothing-found')}
                                        </span>
                                ) :
                                    null
                                )
                        }
                    </div>
                </Layout>
                {
                    !breakpoints.md ?
                        <div
                            style={
                                {
                                    display: 'flex',
                                    padding: '0 2rem',
                                    alignItems: 'center',
                                    minHeight: '57px',
                                    borderTop: '1px solid #ddd',
                                    background: '#fff',
                                    boxShadow: '0rem 0rem 2rem rgba(0,0,0,0.075)',
                                    position: 'relative',
                                    zIndex: 1
                                }
                            }
                        >
                            <div
                                style={
                                    {
                                        display: 'flex',
                                        alignItems: 'center',
                                        width: '100%'
                                    }
                                }
                            >
                                {
                                    providersSelected.length !== 0 ?
                                        <div className="d-flex py-2" style={{alignItems: 'center'}}>
                                            <span
                                                className="d-flex"
                                                style={
                                                    {
                                                        fontSize: '0.85rem'
                                                    }
                                                }
                                            >
                                                <span>
                                                    <span
                                                        style={
                                                            {
                                                                fontWeight: '500',
                                                            }
                                                        }
                                                    >
                                                        {providersSelected.length}
                                                    </span>{t('Providers.results-selected')}</span>
                                            </span>
                                            <span className="ml-4">
                                                <Button
                                                    disabled={modeProvidersCheckout || providersSelected.length === 0}
                                                    size="sm"
                                                    title={t('Providers.view-selected-providers')}
                                                    onClick={
                                                        () => setModeProvidersCheckout(true)
                                                    }
                                                >
                                                    <span>
                                                        <FontAwesomeIcon icon={faEye} />
                                                    </span>
                                                    <span className="ml-2">
                                                        <span>{t('Providers.view-selected')}</span>
                                                    </span>
                                                </Button>
                                            </span>
                                        </div> :
                                        <span
                                            className="py-3"
                                            style={
                                                {
                                                    fontSize: '0.85rem',
                                                    lineHeight: 1.8
                                                }
                                            }
                                        >
                                            {APIDataProviders.providers.length > 0 ? <span>{t('Providers.select-function-intro')}</span> : null}
                                        </span>
                                }
                            </div>
                        </div>
                        : null
                }
            </Layout>

        </LayoutSidebar>
    </>
}

export default Providers;
